/**
 * Typisierter `fetch`-Wrapper für `/api/v2`.
 *
 * Vertrag (siehe Plan, „Canonical API contract details“):
 *  - Session-Cookie, `credentials: 'same-origin'` (Single-Origin, kein CORS).
 *  - Jede Nicht-GET-Anfrage trägt den CSRF-Header `X-Selwa-CSRF: 1`.
 *  - Fehler kommen als `{"error": {code, message, details?}}` und werden in
 *    einen `ApiError` übersetzt.
 *  - `401` (außer beim Login selbst) → Auth-Store leeren und zur Anmeldung
 *    umleiten, inklusive `?redirect=<aktueller Pfad>`.
 */
import { messageForCode } from '@/strings/de'

import type { ApiErrorBody, DownloadedFile } from './types'

export const API_BASE = '/api/v2'

/** Pflicht-Header auf allen zustandsverändernden Anfragen. */
export const CSRF_HEADER = 'X-Selwa-CSRF'
export const CSRF_HEADER_VALUE = '1'

export type QueryValue = string | number | boolean | null | undefined

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  /** Wird als JSON gesendet (außer `undefined`). */
  body?: unknown
  query?: Record<string, QueryValue>
  headers?: Record<string, string>
  signal?: AbortSignal
  /**
   * Unterdrückt die 401-Behandlung (Store leeren + Redirect). Wird für
   * `POST /auth/login` und `GET /auth/me` beim Start gesetzt.
   */
  skipAuthRedirect?: boolean
}

/** Ein vom Server (oder vom Netzwerk) gemeldeter Fehler. */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: unknown

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
    // Damit `instanceof` auch nach dem TS-Downlevel funktioniert.
    Object.setPrototypeOf(this, ApiError.prototype)
  }

  /** Deutsche Meldung für die Oberfläche. */
  get germanMessage(): string {
    return messageForCode(this.code)
  }

  /** Kein HTTP-Status → Netzwerk-/Abbruchfehler. */
  get isNetworkError(): boolean {
    return this.status === 0
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue
    params.append(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

function buildInit(options: RequestOptions): RequestInit {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  }

  // Kanonischer CSRF-Schutz: Header auf allen Nicht-GET-Anfragen.
  if (method !== 'GET') {
    headers[CSRF_HEADER] = CSRF_HEADER_VALUE
  }

  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers,
  }
  if (options.signal) init.signal = options.signal

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(options.body)
  }

  return init
}

/** Baut aus einer Fehlerantwort einen `ApiError`. */
async function toApiError(response: Response): Promise<ApiError> {
  let code = String(response.status)
  let message = response.statusText || 'HTTP error'
  let details: unknown

  try {
    const payload = (await response.json()) as Partial<ApiErrorBody> | null
    const envelope = payload?.error
    if (envelope && typeof envelope === 'object') {
      if (typeof envelope.code === 'string' && envelope.code) code = envelope.code
      if (typeof envelope.message === 'string' && envelope.message) message = envelope.message
      if (envelope.details !== undefined) details = envelope.details
    }
  } catch {
    // Kein JSON-Body (z. B. Proxy-Fehlerseite) – Status-Defaults behalten.
  }

  return new ApiError(code, message, response.status, details)
}

/**
 * Sitzung abgelaufen: Store leeren, zur Anmeldung mit `?redirect=` umleiten.
 * Router und Store werden dynamisch geladen, damit `http.ts` keinen
 * Import-Zyklus mit `@/router` (→ Views → `@/api`) erzeugt.
 */
async function handleUnauthorized(): Promise<void> {
  try {
    const [{ default: router }, { useAuthStore }] = await Promise.all([
      import('@/router'),
      import('@/stores/auth'),
    ])
    useAuthStore().clear()

    const current = router.currentRoute.value
    if (current.name === 'signin') return
    const redirect = current.fullPath
    await router.push({
      name: 'signin',
      query: redirect && redirect !== '/' ? { redirect } : {},
    })
  } catch {
    // Im Test-/SSR-Kontext ohne Router: still ignorieren.
  }
}

async function parseJson<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.status === 205) return undefined as T
  const text = await response.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

/** Führt eine JSON-Anfrage gegen `/api/v2` aus. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, options.query)

  let response: Response
  try {
    response = await fetch(url, buildInit(options))
  } catch (cause) {
    throw new ApiError(
      'NETWORK_ERROR',
      cause instanceof Error ? cause.message : 'Network request failed',
      0,
    )
  }

  if (!response.ok) {
    if (response.status === 401 && !options.skipAuthRedirect) {
      await handleUnauthorized()
    }
    throw await toApiError(response)
  }

  return parseJson<T>(response)
}

/**
 * Lädt eine Datei herunter (Export). Liefert Blob plus Dateiname aus
 * `Content-Disposition`.
 */
export async function httpBlob(
  path: string,
  options: RequestOptions = {},
  fallbackFilename = 'download',
): Promise<DownloadedFile> {
  const url = buildUrl(path, options.query)
  const init = buildInit(options)
  ;(init.headers as Record<string, string>).Accept = '*/*'

  let response: Response
  try {
    response = await fetch(url, init)
  } catch (cause) {
    throw new ApiError(
      'NETWORK_ERROR',
      cause instanceof Error ? cause.message : 'Network request failed',
      0,
    )
  }

  if (!response.ok) {
    if (response.status === 401 && !options.skipAuthRedirect) {
      await handleUnauthorized()
    }
    throw await toApiError(response)
  }

  const blob = await response.blob()
  const filename =
    parseContentDisposition(response.headers.get('Content-Disposition')) ?? fallbackFilename
  return { blob, filename }
}

/**
 * Liest den Dateinamen aus einem `Content-Disposition`-Header.
 * Unterstützt `filename="…"` und RFC 5987 `filename*=UTF-8''…`.
 */
export function parseContentDisposition(header: string | null): string | null {
  if (!header) return null

  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header)
  if (extended?.[2]) {
    try {
      return decodeURIComponent(extended[2].trim())
    } catch {
      return extended[2].trim()
    }
  }

  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header)
  if (quoted?.[1]) return quoted[1]

  const bare = /filename\s*=\s*([^;]+)/i.exec(header)
  if (bare?.[1]) return bare[1].trim()

  return null
}

/** Startet den Browser-Download für eine bereits geladene Datei. */
export function saveBlob(file: DownloadedFile): void {
  const objectUrl = URL.createObjectURL(file.blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = file.filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(objectUrl)
}

export const http = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method'> = {}) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

export default http
